/**
 * Блок управления метками клиента (BUG-138).
 * Отображает чипы текущих меток + autocomplete для назначения новых.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Button, Empty, Input, message, Select, Space, Tag, Tooltip, Typography } from 'antd';
import { CloseOutlined, PlusOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import {
  ClientTag,
  UserTag,
  assignUserTag,
  listClientTags,
  listUserTags,
  unassignUserTag,
} from '../services/clientTagsApi';

const { Text } = Typography;

const ASSIGNED_BY_LABEL: Record<string, string> = {
  manual: 'Назначено вручную',
  auto: 'Назначено автоматически',
  system: 'Системная',
};

interface Props {
  userId: number;
}

const ClientTagsBlock: React.FC<Props> = ({ userId }) => {
  const [tags, setTags] = useState<ClientTag[]>([]);
  const [userTags, setUserTags] = useState<UserTag[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedTagId, setSelectedTagId] = useState<number | undefined>();
  const [reason, setReason] = useState('');
  const [assigning, setAssigning] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [catalog, mine] = await Promise.all([
        listClientTags(false),
        listUserTags(userId),
      ]);
      setTags(catalog);
      setUserTags(mine);
    } catch (e: any) {
      message.error(e?.response?.data?.detail || 'Не удалось загрузить метки');
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    load();
  }, [load]);

  const assigned = new Set(userTags.map((ut) => ut.tag_id));
  const available = tags.filter((t) => !assigned.has(t.id));

  const onAssign = async () => {
    if (!selectedTagId) return;
    setAssigning(true);
    try {
      await assignUserTag(userId, selectedTagId, reason || undefined);
      message.success('Метка назначена');
      setSelectedTagId(undefined);
      setReason('');
      load();
    } catch (e: any) {
      message.error(e?.response?.data?.detail || 'Ошибка назначения');
    } finally {
      setAssigning(false);
    }
  };

  const onUnassign = async (tagId: number) => {
    try {
      await unassignUserTag(userId, tagId);
      message.success('Метка снята');
      load();
    } catch (e: any) {
      message.error(e?.response?.data?.detail || 'Ошибка');
    }
  };

  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        {userTags.length === 0 ? (
          <Empty description="Метки не назначены" image={Empty.PRESENTED_IMAGE_SIMPLE} />
        ) : (
          <Space wrap size={[8, 8]}>
            {userTags.map((ut) => {
              const tooltip = `${ASSIGNED_BY_LABEL[ut.assigned_by] || ut.assigned_by} · ${dayjs(ut.assigned_at).format('DD.MM.YYYY')}${
                ut.reason ? ` · ${ut.reason}` : ''
              }`;
              return (
                <Tooltip key={ut.id} title={tooltip}>
                  <Tag
                    color={ut.tag.color}
                    closable
                    closeIcon={<CloseOutlined />}
                    onClose={(e) => {
                      e.preventDefault();
                      onUnassign(ut.tag_id);
                    }}
                    style={{ fontSize: 13, padding: '2px 10px' }}
                  >
                    {ut.tag.name}
                  </Tag>
                </Tooltip>
              );
            })}
          </Space>
        )}
      </div>

      <Space.Compact style={{ width: '100%' }}>
        <Select
          showSearch
          placeholder="Выберите метку"
          loading={loading}
          value={selectedTagId}
          onChange={setSelectedTagId}
          options={available.map((t) => ({
            value: t.id,
            label: (
              <Space>
                <span
                  style={{
                    display: 'inline-block',
                    width: 10,
                    height: 10,
                    borderRadius: 2,
                    background: t.color,
                  }}
                />
                {t.name}
              </Space>
            ),
          }))}
          filterOption={(input, option) =>
            (option?.label as any)?.props?.children?.[1]
              ?.toLowerCase?.()
              ?.includes(input.toLowerCase()) ?? false
          }
          style={{ width: 240 }}
          disabled={!available.length}
        />
        <Input
          placeholder="Причина (опционально)"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          maxLength={255}
          style={{ flex: 1 }}
        />
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={onAssign}
          loading={assigning}
          disabled={!selectedTagId}
        >
          Назначить
        </Button>
      </Space.Compact>
      {!available.length && tags.length > 0 && (
        <Text type="secondary" style={{ display: 'block', marginTop: 8 }}>
          Все доступные метки уже назначены клиенту.
        </Text>
      )}
      {tags.length === 0 && !loading && (
        <Text type="secondary" style={{ display: 'block', marginTop: 8 }}>
          В тенанте пока нет ни одной метки. Создайте их в разделе «Метки клиентов».
        </Text>
      )}
    </div>
  );
};

export default ClientTagsBlock;
